<script setup lang="ts">
const emit = defineEmits(["close"]);
const userStore = useUserStore();
const { trackEvent } = useGAnalytics();
const { toaster } = useToaster();
const { getUserAvailableProfileBanners, updateUserProfileBanner } =
  useUserProfileController();
const isLoading = ref(false);
const currentBannerIndex = computed<number | null>(() => {
  if (!userStore.profileBanner) return null;
  const idx = banners.value.findIndex((img) => img === userStore.profileBanner);
  return idx >= 0 ? idx : null;
});
const selectedImage = ref<string | null>(userStore.profileBanner || null);

const { data: banners } = await useAsyncData<string[]>(
  () => getUserAvailableProfileBanners(),
  { default: () => [] }
);

const exclusiveERbanners = [
  "banner1",
  "banner2",
  "banner3",
  "banner4",
  "banner5",
  "banner6",
];
const isExclusiveERBanner = (banner: string) =>
  exclusiveERbanners.includes(banner);

const filteredBanners = computed(() => {
  const nonExclusiveBanners = banners.value.filter(
    (banner) => !isExclusiveERBanner(banner)
  );

  if (!userStore.elementalRaidersPlayer) return nonExclusiveBanners;

  const exclusiveBanners = banners.value.filter((banner) =>
    isExclusiveERBanner(banner)
  );

  return [...nonExclusiveBanners, ...exclusiveBanners];
});

const handleSelect = (banner: string) => {
  if (selectedImage.value === banner) {
    selectedImage.value = null;
  } else {
    selectedImage.value = banner;
  }
};

const setDefaultPicture = async () => {
  isLoading.value = true;
  try {
    const res = await updateUserProfileBanner("banner0");
    if (res.statusCode === 200) {
      toaster("success", "profileBannerSet");
    } else {
      toaster("error");
    }
  } catch (error) {
    toaster("error");
  } finally {
    isLoading.value = false;
    emit("close");
  }
};

const handlesetProfileBanner = async () => {
  trackEvent("set_banner");
  isLoading.value = true;
  try {
    if (selectedImage.value === null) {
      setDefaultPicture();
    } else if (selectedImage.value === userStore.profileBanner) {
      toaster("info", "profileBannerUnchanged");
      emit("close");
      return;
    } else {
      const res = await updateUserProfileBanner(
        selectedImage.value || "banner0"
      );
      if (res.statusCode === 200) {
        trackEvent("set_banner_success");
        toaster("success", "profileBannerSet");
      } else {
        trackEvent("set_banner_error");
        toaster("error");
      }
    }
  } catch (error) {
    trackEvent("set_banner_error");
    toaster("error");
  } finally {
    isLoading.value = false;
    emit("close");
  }
};
</script>
<template>
  <BaseLayoutModal width="xl" @close="$emit('close')">
    <template #header>
      {{ $t("main.modalProfileBanners.setYourProfileBanner") }}
    </template>
    <template #content>
      <div class="flex flex-col gap-6">
        <h3 class="text-neutral-300 text-center text-lg">
          {{ $t("main.modalProfileBanners.chooseYourProfileBanner") }}
        </h3>
        <div
          class="flex flex-col gap-2 items-center max-h-[550px] overflow-y-auto">
          <template v-for="(banner, index) in filteredBanners" :key="banner">
            <div
              class="relative cursor-pointer sm:w-[510px] h-28"
              :data-test="`button-select-profile-banner-index-${index}`"
              @click="handleSelect(banner)">
              <img
                :src="getUserBannerSmall(banner)"
                class="p-2 w-full h-full object-cover object-top"
                :class="
                  selectedImage === banner
                    ? 'border-2 border-primary-500'
                    : 'border-2 border-secondary-500'
                " />
              <img
                v-if="selectedImage === banner"
                src="/img/icon_tick.png"
                class="absolute bottom-3 right-3 w-8" />
            </div>
          </template>
        </div>
      </div>
      <div class="mt-5 mb-2 sm:mt-6 flex">
        <div class="w-full flex flex-col items-center justify-center gap-4">
          <BaseButtonSubmit
            :text="$t('main.modalProfileBanners.setProfileBanner')"
            size="long"
            class="!w-[230px]"
            :loading="isLoading"
            :disabled="isLoading"
            data-test="button-set-profile-banner"
            @click="handlesetProfileBanner" />
          <p
            class="text-primary-500 text-sm cursor-pointer"
            data-test="button-set-default-profile-banner"
            @click="setDefaultPicture">
            {{ $t("main.modalProfileBanners.useDefaultBanner") }}
          </p>
        </div>
      </div>
    </template>
  </BaseLayoutModal>
</template>
